import {
  patchState,
  signalStore,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import {
  removeEntity,
  setAllEntities,
  withEntities,
} from '@ngrx/signals/entities';
import { ArticleFile } from '@knust/api-interfaces';
import { HttpClient } from '@angular/common/http';
import { DOCUMENT, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ARTICLES_URL } from '../constants/api.constants';

type ArticleFilesState = {
  loading: boolean;
  uploading: boolean;
};

const initialState: ArticleFilesState = {
  loading: false,
  uploading: false,
};

const articleFilesUrl = (articleId: number) =>
  ARTICLES_URL + '/' + articleId + '/files';

/**
 * PDF-Dateien eines Artikels. Der Store wird von der ArticleFilesComponent bereitgestellt, damit jede
 * Artikeldetailseite ihren eigenen Zustand hat.
 * Fehler werden bereits vom HttpErrorInterceptor gemeldet, der die Antwort dann leer abschließt.
 */
export const ArticleFilesStore = signalStore(
  withState<ArticleFilesState>(initialState),
  withEntities<ArticleFile>(),
  withProps(() => ({
    http: inject(HttpClient),
    document: inject(DOCUMENT),
  })),
  withMethods((store) => ({
    async load(articleId: number) {
      patchState(store, { loading: true });

      try {
        const files = await lastValueFrom(
          store.http.get<ArticleFile[]>(articleFilesUrl(articleId)),
          { defaultValue: [] },
        );

        patchState(store, setAllEntities(files));
      } finally {
        patchState(store, { loading: false });
      }
    },
    async upload(articleId: number, file: File, label: string): Promise<boolean> {
      const formData = new FormData();
      formData.append('label', label);
      formData.append('file', file);

      patchState(store, { uploading: true });

      try {
        const articleFile = await lastValueFrom(
          store.http.post<ArticleFile>(articleFilesUrl(articleId), formData),
          { defaultValue: undefined },
        );

        if (!articleFile) {
          return false;
        }

        // Neueste Datei zuerst, passend zur Sortierung der API
        patchState(store, setAllEntities([articleFile, ...store.entities()]));

        return true;
      } finally {
        patchState(store, { uploading: false });
      }
    },
    async remove(articleId: number, articleFile: ArticleFile) {
      const response = await lastValueFrom(
        store.http.delete(articleFilesUrl(articleId) + '/' + articleFile.id),
        { defaultValue: undefined },
      );

      if (response !== undefined) {
        patchState(store, removeEntity(articleFile.id));
      }
    },
    // Der Download läuft über den HttpClient, damit das Token mitgesendet wird
    async download(articleId: number, articleFile: ArticleFile) {
      const blob = await lastValueFrom(
        store.http.get(articleFilesUrl(articleId) + '/' + articleFile.id, {
          responseType: 'blob',
        }),
        { defaultValue: undefined },
      );

      if (!blob) {
        return;
      }

      const objectUrl = URL.createObjectURL(blob);
      const link = store.document.createElement('a');
      link.href = objectUrl;
      link.download = articleFile.label + '.pdf';
      link.click();

      setTimeout(() => URL.revokeObjectURL(objectUrl));
    },
  })),
);
